import {isMacOS} from '@github-ui/get-os'
import {colorNames} from '@github-ui/use-named-color'

import {MAX_OPTION_NAME_LENGTH, VALIDATION_MESSAGES} from './resources'
import type {NewSingleSelectOption, SingleSelectOptionWithId} from './types'

const NEW_SINGLE_SELECT_OPTION_ID_MATCHER = 'single-select-option'

export interface ValidationError {
  type: 'required' | 'tooLong' | 'duplicate'
  message: string
}

export const getValidationErrors = (
  name: string,
  options: SingleSelectOptionWithId[],
  noDuplicatedOptions: boolean,
  enableNameLengthValidation: boolean = false,
  currentOptionName?: string,
): ValidationError[] => {
  const errors: ValidationError[] = []

  if (!name.trim()) {
    errors.push({
      type: 'required',
      message: VALIDATION_MESSAGES.REQUIRED,
    })
  }

  // Check length validation if enabled
  if (enableNameLengthValidation && name.length > MAX_OPTION_NAME_LENGTH) {
    errors.push({
      type: 'tooLong',
      message: VALIDATION_MESSAGES.TOO_LONG,
    })
  }

  // Check for duplicates if enabled
  if (noDuplicatedOptions && name.trim()) {
    const hasDuplicate = options.some(option => {
      const isSameAsCurrentOption = currentOptionName
        ? option.name.toLowerCase() === currentOptionName.toLowerCase()
        : false
      return option.name.toLowerCase() === name.toLowerCase() && !isSameAsCurrentOption
    })

    if (hasDuplicate) {
      errors.push({
        type: 'duplicate',
        message: VALIDATION_MESSAGES.DUPLICATE,
      })
    }
  }

  return errors
}

export const isValidForSubmission = (
  name: string,
  options: SingleSelectOptionWithId[],
  noDuplicatedOptions: boolean,
  enableNameLengthValidation: boolean = false,
  currentOptionName?: string,
): boolean => {
  const errors = getValidationErrors(name, options, noDuplicatedOptions, enableNameLengthValidation, currentOptionName)
  const hasBlockingErrors = errors.some(
    error => error.type === 'required' || error.type === 'duplicate' || error.type === 'tooLong',
  )

  return name.trim() !== '' && !hasBlockingErrors
}

export const isPlatformMeta = (e: React.MouseEvent | React.KeyboardEvent | PointerEvent): boolean => {
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
  return isMacOS() ? e.metaKey : e.ctrlKey
}

export const buildNewSingleSelectOption = (
  id: number,
  option: NewSingleSelectOption,
  options: SingleSelectOptionWithId[],
) => {
  // filter options to see if one option has color
  if (option.color === 'GRAY' && options.some(o => o.color !== 'GRAY')) {
    const availableColors = colorNames.slice(1) // remove gray

    const randomColor = availableColors[Math.floor(Math.random() * availableColors.length)] || 'PURPLE'
    option = {...option, color: randomColor}
  }
  return {...option, id: `${NEW_SINGLE_SELECT_OPTION_ID_MATCHER}-${id}`}
}
